import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useStateContext } from '@xkit-yx/im-kit-ui';
import MessageRow from '../../../chat/messages/MessageRow';
import useEmojiConfig from '../../../chat/hooks/useEmojiConfig';
import useTeamChatMeta from '../../../chat/hooks/useTeamChatMeta';
import { RedEnvelopeProvider } from '../../../chat/redEnvelope/RedEnvelopeContext';
import '../../../chat/css/ChatMobile.css';

const PULL_THRESHOLD = 44;
const PAGE_SIZE = 50;

/**
 * 只读聊天历史查看页。
 *
 * props.query 参数：
 *   tid       – 群 ID
 *   time      – 起始时间戳（ms），从此时间点向后加载第一批消息
 *   idClient? – 消息 idClient；若提供，初始加载完毕后自动滚动到该消息
 */
const ChatHistoryViewerInner = observer(function ChatHistoryViewerInner(props) {
  const { tid, time: timeStr, idClient: targetIdClient } = props.query;
  const targetTime = parseInt(timeStr, 10);
  const sessionId  = `team-${tid}`;

  const { store, nim } = useStateContext();
  const { getEmojiConfig } = useEmojiConfig();
  const meta = useTeamChatMeta({ store, nim, tid, sessionId });

  const [msgs,          setMsgs]          = useState([]);
  const [hasMoreOlder,  setHasMoreOlder]  = useState(true);
  const [hasMoreNewer,  setHasMoreNewer]  = useState(true);
  const [loadingOlder,  setLoadingOlder]  = useState(false);
  const [loadingNewer,  setLoadingNewer]  = useState(false);
  const [initialized,   setInitialized]   = useState(false);

  const listRef          = useRef(null);
  const pullIndicatorRef = useRef(null);
  const hasInitedRef     = useRef(false);

  // ref 镜像（避免 effect 内闭包过期）
  const loadingOlderRef      = useRef(false);
  const loadingNewerRef      = useRef(false);
  const hasMoreOlderRef      = useRef(true);
  const hasMoreNewerRef      = useRef(true);
  const msgsRef              = useRef([]);
  const initializedRef       = useRef(false); // 初始加载完成标志（给 touch 回调用）
  const pendingScrollAdjust  = useRef(null);  // 下拉加载更旧后需补偿的 prevScrollHeight

  useEffect(() => { loadingOlderRef.current = loadingOlder; }, [loadingOlder]);
  useEffect(() => { loadingNewerRef.current = loadingNewer; }, [loadingNewer]);
  useEffect(() => { hasMoreOlderRef.current = hasMoreOlder; }, [hasMoreOlder]);
  useEffect(() => { hasMoreNewerRef.current = hasMoreNewer; }, [hasMoreNewer]);
  useEffect(() => { msgsRef.current = msgs; }, [msgs]);
  useEffect(() => { initializedRef.current = initialized; }, [initialized]);

  // 在浏览器绘制前同步修正 scrollTop，消除下拉加载时的页面跳动
  useLayoutEffect(() => {
    if (pendingScrollAdjust.current === null) return;
    const prevHeight = pendingScrollAdjust.current;
    pendingScrollAdjust.current = null;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight - prevHeight;
  }, [msgs]);

  // ── pull indicator DOM 操作 ────────────────────────────────────────────────
  const applyPullDOM = useCallback((distance, isReady, animated = false) => {
    const el = pullIndicatorRef.current;
    if (!el) return;
    el.style.transition = animated ? '' : 'none';
    el.style.height     = distance + 'px';
    el.classList.toggle('imc-pull-ready', isReady);
  }, []);

  // ── 初始加载 ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!props.active || hasInitedRef.current) return;
    hasInitedRef.current = true;
    meta.initOnce();

    const init = async () => {
      try {
        const res = await nim.getHistoryMsgs({
          scene:     'team',
          to:        tid,
          beginTime: targetTime,
          reverse:   true,  // true = 从 beginTime 向后（更新方向）查
          limit:     PAGE_SIZE,
        });
        const sorted = (res || []).sort((a, b) => a.time - b.time);
        setMsgs(sorted);
        setHasMoreNewer((res || []).length >= PAGE_SIZE);
      } catch (e) {
        // ignore
      } finally {
        setInitialized(true);
      }
    };
    init();
  // eslint-disable-next-line
  }, [props.active]);

  // ── 初始化完成后滚动到目标消息 ───────────────────────────────────────────
  useEffect(() => {
    if (!initialized) return;
    if (!targetIdClient) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const list = listRef.current;
        if (!list) return;
        const el = list.querySelector(`[data-msg-id="${targetIdClient}"]`);
        if (!el) return;
        const prev   = el.previousElementSibling;
        const anchor = (prev && prev.classList.contains('imc-time-separator')) ? prev : el;
        const listRect   = list.getBoundingClientRect();
        const anchorRect = anchor.getBoundingClientRect();
        list.scrollTop = Math.max(0, list.scrollTop + (anchorRect.top - listRect.top) - 8);
      });
    });
  // eslint-disable-next-line
  }, [initialized]);

  // ── 加载更旧消息 ─────────────────────────────────────────────────────────
  const loadOlder = useCallback(async () => {
    if (loadingOlderRef.current || !hasMoreOlderRef.current) return;
    const list = msgsRef.current;
    if (!list.length) return; // 初始加载未完成，静默忽略，不修改 hasMoreOlder

    setLoadingOlder(true);
    const el         = listRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    try {
      const res = await nim.getHistoryMsgs({
        scene:     'team',
        to:        tid,
        endTime:   list[0].time,
        lastMsgId: list[0].idServer,
        reverse:   false, // false = 从 endTime 向前（更旧方向）查
        limit:     PAGE_SIZE,
      });
      const older = (res || []).sort((a, b) => a.time - b.time);
      if ((res || []).length < PAGE_SIZE) setHasMoreOlder(false);
      if (older.length > 0) {
        // 在 setMsgs 前存入 prevHeight，useLayoutEffect 将在绘制前同步修正 scrollTop
        pendingScrollAdjust.current = prevHeight;
        setMsgs(prev => {
          const map = new Map();
          for (const m of [...older, ...prev]) map.set(m.idClient, m);
          return Array.from(map.values()).sort((a, b) => a.time - b.time);
        });
      }
    } catch (e) {
      setHasMoreOlder(false);
    } finally {
      const ind = pullIndicatorRef.current;
      if (ind) ind.style.transition = '';
      setLoadingOlder(false);
    }
  }, [nim, tid]);

  // ── 加载更新消息 ─────────────────────────────────────────────────────────
  const loadNewer = useCallback(async () => {
    if (loadingNewerRef.current || !hasMoreNewerRef.current) return;
    const list = msgsRef.current;
    if (!list.length) return;

    setLoadingNewer(true);
    try {
      const res = await nim.getHistoryMsgs({
        scene:     'team',
        to:        tid,
        beginTime: list[list.length - 1].time,
        lastMsgId: list[list.length - 1].idServer,
        reverse:   true,  // true = 从 beginTime 向后（更新方向）查
        limit:     PAGE_SIZE,
      });
      const newer = (res || []).sort((a, b) => a.time - b.time);
      if ((res || []).length < PAGE_SIZE) setHasMoreNewer(false);
      if (newer.length > 0) {
        setMsgs(prev => {
          const map = new Map();
          for (const m of [...prev, ...newer]) map.set(m.idClient, m);
          return Array.from(map.values()).sort((a, b) => a.time - b.time);
        });
      }
    } catch (e) {
      setHasMoreNewer(false);
    } finally {
      setLoadingNewer(false);
    }
  }, [nim, tid]);

  // ── 下拉加载更旧（触摸手势）─────────────────────────────────────────────
  const pullStartY      = useRef(0);
  const isPulling       = useRef(false);
  const pullDistanceRef = useRef(0);
  const pullIsReadyRef  = useRef(false);
  const rafRef          = useRef(null);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      if (!initializedRef.current) return; // 初始加载未完成时不触发
      if (el.scrollTop > 16) return;
      if (loadingOlderRef.current || !hasMoreOlderRef.current) return;
      pullStartY.current      = e.touches[0].clientY;
      isPulling.current       = true;
      pullDistanceRef.current = 0;
      pullIsReadyRef.current  = false;
    };

    const onTouchMove = (e) => {
      if (!isPulling.current) return;
      const deltaY = e.touches[0].clientY - pullStartY.current;
      if (deltaY <= 0) { cancelAnimationFrame(rafRef.current); applyPullDOM(0, false); return; }
      e.preventDefault();
      const damped          = Math.min(deltaY * 0.45, PULL_THRESHOLD * 1.5);
      pullDistanceRef.current = damped;
      pullIsReadyRef.current  = damped >= PULL_THRESHOLD;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        applyPullDOM(pullDistanceRef.current, pullIsReadyRef.current);
      });
    };

    const onTouchEnd = () => {
      if (!isPulling.current) return;
      isPulling.current = false;
      cancelAnimationFrame(rafRef.current);
      if (pullIsReadyRef.current && !loadingOlderRef.current) {
        const ind = pullIndicatorRef.current;
        if (ind) ind.style.transition = 'none';
        loadOlder();
      } else {
        applyPullDOM(0, false, true);
      }
      pullDistanceRef.current = 0;
      pullIsReadyRef.current  = false;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true  });
    el.addEventListener('touchmove',  onTouchMove,  { passive: false });
    el.addEventListener('touchend',   onTouchEnd,   { passive: true  });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove',  onTouchMove);
      el.removeEventListener('touchend',   onTouchEnd);
      cancelAnimationFrame(rafRef.current);
    };
  // eslint-disable-next-line
  }, [applyPullDOM, loadOlder]);

  // ── 滚动到底部时加载更新消息（避免 IntersectionObserver 在初始渲染时立即触发） ──
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      if (!initializedRef.current) return;
      if (loadingNewerRef.current || !hasMoreNewerRef.current) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) {
        loadNewer();
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  // eslint-disable-next-line
  }, [loadNewer]);

  // ── 空操作（只读页面无消息操作）─────────────────────────────────────────
  const noop         = useCallback(() => {}, []);
  const emptyActions = useCallback(() => [], []);

  // ── 渲染 ─────────────────────────────────────────────────────────────────
  return (
    <div className="im-mobile-chat">
      <div className="imc-msg-list" ref={listRef}>

        {/* 顶部下拉加载更旧指示器（始终挂载，保证 ref 有效） */}
        <div
          ref={pullIndicatorRef}
          className="imc-pull-indicator"
          style={{ height: loadingOlder ? 44 : 0, display: hasMoreOlder ? undefined : 'none' }}
        >
          <div className="imc-pull-inner"><span className="imc-pull-spinner" /></div>
        </div>

        {/* 初始化中占位 */}
        {!initialized && (
          <div className="imc-viewer-loading">
            <span className="imc-pull-spinner" />
          </div>
        )}

        {/* 消息列表 */}
        {(() => {
          const currentNickMembers = meta.nickMembers();
          return msgs.map((msg, index) => (
            <MessageRow
              key={msg.idClient || index}
              msg={msg}
              isCollapsing={false}
              prevMsgTime={index > 0 ? msgs[index - 1].time : null}
              isActionTarget={false}
              isPlusOne={false}
              setActionTarget={noop}
              getActions={emptyActions}
              onMessageAction={noop}
              handlePlusOne={noop}
              insertMention={noop}
              nickMembers={currentNickMembers}
              findAccountFilter={meta.findAccountFilterByAccount}
              store={store}
              getEmojiConfig={getEmojiConfig}
            />
          ));
        })()}

        {/* 底部：加载更新消息 loading 指示 */}
        {initialized && hasMoreNewer && loadingNewer && (
          <div className="imc-viewer-newer-sentinel">
            <div className="imc-pull-inner"><span className="imc-pull-spinner" /></div>
          </div>
        )}
        {initialized && !hasMoreNewer && msgs.length > 0 && (
          <div className="imc-viewer-end">已显示至最新消息</div>
        )}

        {/* 无数据占位 */}
        {initialized && msgs.length === 0 && (
          <div className="imc-viewer-empty">暂无消息</div>
        )}
      </div>
    </div>
  );
});

// 外层：提供 RedEnvelopeProvider，使 RedPacketMessage/TipMessage 等组件可正常使用 useRedEnvelope
export default function ChatHistoryViewer(props) {
  return (
    <RedEnvelopeProvider>
      <ChatHistoryViewerInner {...props} />
    </RedEnvelopeProvider>
  );
}
